// src/components/modals/PromotionsModal.jsx
import { useState, useEffect, useCallback } from 'react'
import { X, Tag, Clock, Percent, DollarSign } from 'lucide-react'
import PromotionService from '../../services/promotionService'
import { toast } from 'react-hot-toast'

const PromotionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('product') // 'product' or 'order'
  const [promotions, setPromotions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Define the fetch function first
  const fetchPromotionsByType = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log('🎯 Fetching promotions for tab:', activeTab)
      
      const promotionType = activeTab === 'product' ? 'product_based' : 'order_based'
      
      // Use specific parameters based on the API documentation
      const response = await PromotionService.getActivePromotions({
        isActive: true,                // Active filter
        promotionType: promotionType,  // Filter by type
        limit: 50,                     // Reasonable limit
        page: 1                        // First page
      })
      
      console.log('🔍 API response for', promotionType, ':', response)
      console.log('🔍 Response data:', response?.data)
      console.log('🔍 Response success:', response?.success)
      console.log('🔍 Response type:', typeof response)
      console.log('🔍 Response keys:', Object.keys(response || {}))
      
      // Use the same pattern as AllPromotions.jsx
      let promotionsData = null
      
      if (response && response.success) {
        // Extract promotions from the nested structure
        promotionsData = response.data?.promotions || response.data || []
        console.log('✅ Using response.data.promotions:', promotionsData.length, 'items')
      } else if (Array.isArray(response?.data)) {
        console.log('✅ Using response.data (array)')
        promotionsData = response.data
      } else if (Array.isArray(response)) {
        console.log('✅ Using response (direct array)')
        promotionsData = response
      }
      
      console.log('🔍 Final promotions data:', promotionsData)
      console.log('🔍 Promotions count:', promotionsData?.length)
      
      // Filter out expired promotions and validate active status (additional client-side validation)
      if (promotionsData && Array.isArray(promotionsData)) {
        const currentDate = new Date()
        const validPromotions = promotionsData.filter(promotion => {
          // Check if promotion is active
          if (!promotion.isActive) {
            console.log('❌ Inactive promotion filtered out:', promotion.name)
            return false
          }
          
          // Check if promotion is within date range
          const startDate = new Date(promotion.startDate)
          const endDate = new Date(promotion.endDate)
          
          if (currentDate < startDate || currentDate > endDate) {
            console.log('❌ Expired promotion filtered out:', promotion.name, {
              current: currentDate.toISOString(),
              start: promotion.startDate,
              end: promotion.endDate
            })
            return false
          }
          
          console.log('✅ Valid promotion:', promotion.name)
          return true
        })
        
        console.log('✅ Setting valid promotions:', validPromotions.length, 'items')
        setPromotions(validPromotions)
      } else {
        console.log('❌ No valid promotions data found')
        setPromotions([])
      }
    } catch (err) {
      console.error('Error fetching promotions:', err)
      setError('Failed to load promotions')
      toast.error('Failed to load promotions')
    } finally {
      setLoading(false)
    }
  }, [activeTab])

  // Fetch promotions when modal opens or tab changes
  useEffect(() => {
    if (isOpen) {
      fetchPromotionsByType()
    }
  }, [isOpen, fetchPromotionsByType])

  // Filter promotions based on active tab
  const getFilteredPromotions = () => {
    console.log('🔍 Filtering promotions:', {
      promotions,
      promotionsLength: promotions?.length,
      activeTab,
      isArray: Array.isArray(promotions)
    })
    
    if (!promotions || !Array.isArray(promotions)) {
      console.log('❌ Promotions is not a valid array')
      return []
    }
    
    const filtered = promotions.filter(promotion => {
      console.log('🔍 Checking promotion:', {
        name: promotion.name,
        promotionType: promotion.promotionType,
        activeTab
      })
      
      if (activeTab === 'product') {
        return promotion.promotionType === 'product_based'
      } else {
        return promotion.promotionType === 'order_based'
      }
    })
    
    console.log('✅ Filtered promotions:', filtered)
    return filtered
  }

  // Format discount display
  const formatDiscount = (promotion) => {
    if (!promotion) return ''
    
    if (promotion.discountType === 'percentage') {
      return `${promotion.discountValue}% Off`
    } else {
      return `Rs.${promotion.discountValue} Off`
    }
  }

  // Format time display
  const formatTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return ''
    
    const formatTime = (timeStr) => {
      try {
        const time = new Date(`2000-01-01T${timeStr}`)
        return time.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        })
      } catch {
        return timeStr
      }
    }
    
    return `${formatTime(startTime)} - ${formatTime(endTime)}`
  }

  // Format days active
  const formatDaysActive = (days) => {
    if (!days || !Array.isArray(days)) return ''
    
    const dayMap = {
      'monday': 'Mon',
      'tuesday': 'Tue',
      'wednesday': 'Wed',
      'thursday': 'Thu',
      'friday': 'Fri',
      'saturday': 'Sat',
      'sunday': 'Sun'
    }
    
    return days.map(day => dayMap[day.toLowerCase()] || day).join(', ')
  }

  // Format time display from conditions
  const getTimeRangeFromConditions = (conditions) => {
    if (!conditions || !Array.isArray(conditions)) return null
    
    const timeCondition = conditions.find(condition => condition.type === 'time_range')
    if (timeCondition && timeCondition.timeRange) {
      return formatTimeRange(timeCondition.timeRange.startTime, timeCondition.timeRange.endTime)
    }
    
    return null
  }

  // Format days active from conditions
  const getDaysActiveFromConditions = (conditions) => {
    if (!conditions || !Array.isArray(conditions)) return null
    
    const daysCondition = conditions.find(condition => condition.type === 'days_active')
    if (daysCondition && daysCondition.daysActive) {
      return formatDaysActive(daysCondition.daysActive)
    }
    
    return null
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[999999] overflow-y-auto" style={{ zIndex: 100002 }}>
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">All Discounts</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('product')}
              className="w-full px-6 py-4 text-center font-medium bg-orange-500 text-white"
            >
              Product Based Promotions
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                <span className="ml-3 text-gray-600">Loading promotions...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-red-500 mb-2">{error}</div>
                <button
                  onClick={fetchPromotionsByType}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {getFilteredPromotions().length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Tag className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">No {activeTab === 'product' ? 'Product' : 'Order'} Promotions</p>
                    <p className="text-sm">No active promotions available for this type.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getFilteredPromotions().map((promotion) => (
                      <div
                        key={promotion._id}
                        className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all duration-200"
                      >
                        {/* Promotion Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">
                              {promotion.name}
                            </h4>
                            <div className="flex items-center space-x-2">
                              {promotion.discountType === 'percentage' ? (
                                <Percent className="h-4 w-4 text-green-600" />
                              ) : (
                                <DollarSign className="h-4 w-4 text-green-600" />
                              )}
                              <span className="text-2xl font-bold text-green-600">
                                {formatDiscount(promotion)}
                              </span>
                            </div>
                          </div>
                          
                          {/* Status Badge */}
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                            Active
                          </span>
                        </div>

                        {/* Promotion Description */}
                        {promotion.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                            {promotion.description}
                          </p>
                        )}

                        {/* Time and Days Information */}
                        <div className="border-t border-gray-100 pt-4">
                          {/* Time Range from conditions */}
                          {getTimeRangeFromConditions(promotion.conditions) && (
                            <div className="flex items-center space-x-2 mb-2">
                              <Clock className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {getTimeRangeFromConditions(promotion.conditions)}
                              </span>
                            </div>
                          )}

                          {/* Days Active from conditions */}
                          {getDaysActiveFromConditions(promotion.conditions) && (
                            <div className="flex items-center space-x-2">
                              <div className="text-sm text-gray-600">
                                <span className="font-medium">Days Active:</span>{' '}
                                {getDaysActiveFromConditions(promotion.conditions)}
                              </div>
                            </div>
                          )}

                          {/* Show promotion period */}
                          <div className="mt-2 text-xs text-gray-500">
                            Valid: {new Date(promotion.startDate).toLocaleDateString()} - {new Date(promotion.endDate).toLocaleDateString()}
                          </div>
                        </div>

                        {/* Priority Badge */}
                        {promotion.priority && (
                          <div className="mt-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                              promotion.priority === 'high' 
                                ? 'bg-red-100 text-red-800'
                                : promotion.priority === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {String(promotion.priority).charAt(0).toUpperCase() + String(promotion.priority).slice(1)} Priority
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PromotionsModal
